import { ChangeEvent, useEffect, useState } from 'react';
import { fetchPDFDetails } from '@api/fetch-pdf-details';
import { IPDFDetailsResponse, TNullable } from '@common/types';
import { FullScreenLoader } from '@components/full-screen-loader';
import ChaptersNavigator from '@components/pdf-viewer/pdf-chapters-navigator';
import PDFDocument from '@components/pdf-viewer/pdf-document';
import Header from '@components/pdf-viewer/pdf-header';
import Toolbar from '@components/pdf-viewer/pdf-toolbar';
import { TChapterInfo, TPageDimensions } from '@components/pdf-viewer/pdf.models';
import { PDF_ZOOM_SCALE } from '@constants/standard-pdf';
import { shouldUseMaxHight } from '@lib/utils';
import { NotFound } from '@pages/not-found';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PDFReadingMode, ScrollSource } from './pdf-viewer-shared-props';

function DesktopPDFViewer() {
  const { t } = useTranslation();
  const { docId } = useParams<{ docId: string }>();
  const [isLoading, setLoading] = useState<boolean>(true);
  const queryParams = new URLSearchParams(location.search);
  const headerId = queryParams.get('header-id');
  const [pdfDetails, setPdfDetails] = useState<TNullable<IPDFDetailsResponse>>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [readingMode, setReadingMode] = useState<PDFReadingMode>(PDFReadingMode.SinglePage);
  const [scrollSource, setScrollSource] = useState<ScrollSource>(ScrollSource.None);
  const [selectedChapter, setSelectedChapter] = useState<TNullable<TChapterInfo>>(null);
  const [selectedPage, setSelectedPage] = useState<number>(selectedChapter?.page || 1);
  const [pageInputValue, setPageInputValue] = useState<'' | number>(selectedChapter?.page || '');
  const [scale, setScale] = useState(1.8);
  const [pageDimensions, setPageDimensions] = useState<TPageDimensions>({
    width: 0,
    height: 0,
  });
  const [isChapterNavigatorOpen, setChapterNavigatorOpen] = useState<boolean>(false);

  useEffect(() => {
    if (docId) {
      fetchPDFDetails(docId)
        .then((result) => {
          if (result) {
            setPdfDetails(result);
            const foundSection = result.section_coordination_list.find(
              (section) => section.header_id === headerId
            );
            if (foundSection) {
              setSelectedChapter({
                number: foundSection.section_number,
                page: foundSection.page,
                coordinates: foundSection.coordinates,
                width: foundSection.width,
                height: foundSection.height,
              });
              setSelectedPage(foundSection.page);
              setScrollSource(ScrollSource.Chapter);
            } else {
              const firstSection = result.section_coordination_list[0];
              // Not an article
              if (firstSection) {
                setSelectedChapter({
                  number: firstSection.section_number,
                  page: firstSection.page,
                  coordinates: firstSection.coordinates,
                  width: firstSection.width,
                  height: firstSection.height,
                });
                setSelectedPage(firstSection.page);
                setScrollSource(ScrollSource.Chapter);
              }
            }
          }
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (typeof pageInputValue === 'number') setSelectedPage(pageInputValue);
  }, [pageInputValue]);

  useEffect(() => {
    setPageInputValue(selectedPage);
  }, [selectedPage]);

  if (isLoading) return <FullScreenLoader title={t('pdf.init')} subtitle={t('pdf.wait')} />;
  if (!docId) return <NotFound />;
  if (!pdfDetails) return <NotFound />;

  const chapters: TChapterInfo[] = pdfDetails.section_coordination_list.map((section) => ({
    title: section.title,
    number: section.section_number,
    page: section.page,
    coordinates: section.coordinates,
    width: section.width,
    height: section.height,
  }));

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setTotalPages(numPages);
  }

  function handlePageRenderSuccess({ width, height }: TPageDimensions) {
    setPageDimensions({ width, height });
  }

  function handleReadingModeChange(newMode: PDFReadingMode) {
    setReadingMode(newMode);
    if (PDFReadingMode.SinglePage === newMode) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    } else {
      setTimeout(() => {
        const currentPage = selectedPage;
        setSelectedPage(currentPage);
        setScrollSource(ScrollSource.Page);
      }, 500);
    }
  }

  function handleSetPage(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    if (value.length === 0) {
      setPageInputValue('');
      return;
    } else if (/^\d+$/.test(value)) {
      const valueAsNumber = Number(value);
      if (valueAsNumber >= 1 && valueAsNumber <= totalPages) {
        setPageInputValue(valueAsNumber);
      }
    }
  }

  function handlePageChange(operation: 'prev' | 'next') {
    setSelectedPage((prev) => (operation === 'prev' ? prev - 1 : prev + 1));
    setScrollSource(ScrollSource.Page);
  }

  function handleChapterChange(chapterNumber: string) {
    const nextChapter = chapters.find((chapter) => chapter.number === chapterNumber);
    if (nextChapter) {
      setSelectedPage(nextChapter.page);
      setSelectedChapter(nextChapter);
      setScrollSource(
        nextChapter.page !== selectedPage ? ScrollSource.PageAndChapter : ScrollSource.Chapter
      );
    }
  }

  function handleZoomIn() {
    setScale((prevScale) => parseFloat(Math.min(prevScale + 0.2, PDF_ZOOM_SCALE.MAX).toFixed(2)));
  }

  function handleZoomOut() {
    setScale((prevScale) => parseFloat(Math.max(prevScale - 0.2, PDF_ZOOM_SCALE.MIN).toFixed(2)));
  }

  const pdfURL = pdfDetails?.pdf_file_url;
  if (!pdfURL) return null;

  return (
    <div className="pdf-viewer w-full">
      <div
        /*className={`fixed top-[80px] left-0 w-96 py-3 bg-slate-100 transform transition-transform duration-300 ease-in-out z-10 ${
          isChapterNavigatorOpen ? 'translate-x-0' : '-translate-x-full'
        }`}*/
        className={`fixed top-[80px] left-0 overflow-x-hidden transition-all duration-300 ease-in-out py-3 bg-slate-100 z-10 ${
          isChapterNavigatorOpen ? 'w-96' : 'w-0'
        }`}
      >
        {selectedChapter && (
          <ChaptersNavigator
            open={isChapterNavigatorOpen}
            onOpenChange={setChapterNavigatorOpen}
            chapters={chapters}
            handleChapterChange={handleChapterChange}
            selectedChapter={selectedChapter}
          />
        )}
      </div>

      <Header pdfName={pdfDetails?.display_name || ''} />
      <Toolbar
        handleReadingModeChange={handleReadingModeChange}
        readingMode={readingMode}
        handlePageChange={handlePageChange}
        handleZoomIn={handleZoomIn}
        handleZoomOut={handleZoomOut}
        handleSetPage={handleSetPage}
        pageInputValue={pageInputValue}
        scale={scale}
        selectedPage={selectedPage}
        setPageInputValue={setPageInputValue}
        totalPages={totalPages}
        chapters={chapters}
        selectedChapter={selectedChapter}
        handleChapterChange={handleChapterChange}
        shouldShowChapterNavigator={pdfDetails.section_coordination_list?.length > 0}
        setChapterNavigatorOpen={setChapterNavigatorOpen}
      />
      <div
        // [hexal]
        className={`${shouldUseMaxHight() ? 'max-h-[500px] overflow-y-scroll' : ''} mt-24 transition-[margin] duration-300 ease-in-out overflow-x-auto ${isChapterNavigatorOpen ? 'ml-96' : 'ml-0'}`}
      >
        <PDFDocument
          handlePageRenderSuccess={handlePageRenderSuccess}
          onDocumentLoadSuccess={onDocumentLoadSuccess}
          pageDimensions={pageDimensions}
          scale={scale}
          originalPageWidth={pdfDetails.page_width}
          originalPageHeight={pdfDetails.page_height}
          url={pdfURL}
          selectedChapter={selectedChapter}
          selectedPage={selectedPage}
          scrollSource={scrollSource}
          setScrollSource={setScrollSource}
          readingMode={readingMode}
          onVisiblePageChange={(page) => setSelectedPage(page)}
        />
      </div>
    </div>
  );
}

export default DesktopPDFViewer;
